import {useCallback, useContext} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {validateAccessRightsProfiles} from 'src/openapi/admin/apis/ValidateAccessRightsProfilesApi';
import {useAppDispatch} from 'src/store/hooks';

import {PROFILE_VALIDATION_ERRORS} from './role-profile/constants';

export const useValidateProfile = () => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError, clearErrors} = useContext(ValidationContext);
    const {setErrorMessage} = useContext(MessageContext);

    const validateProfileName = useCallback(async (profileName: string, initialName: string) => {
        if (!profileName) {
            addError({
                controlId: 'profile-name',
                code: 'NAME_IS_REQUIRED',
                message: PROFILE_VALIDATION_ERRORS.NAME_IS_REQUIRED,
            });
            setErrorMessage(PROFILE_VALIDATION_ERRORS.NAME_IS_REQUIRED);
            return true;
        }

        if (profileName === initialName) {
            removeError('profile-name');
            return false;
        }

        const isUniqueCheck = await apiDispatch(validateAccessRightsProfiles({profileName}));

        if (isUniqueCheck.payload.error) {
            addError({
                controlId: 'profile-name',
                code: 'DUPLICATED_NAME',
                message: PROFILE_VALIDATION_ERRORS.DUPLICATED_NAME,
            });
            setErrorMessage(PROFILE_VALIDATION_ERRORS.DUPLICATED_NAME);
            return true;
        } else {
            removeError('profile-name');
            return false;
        }
    }, [addError, apiDispatch, removeError, setErrorMessage]);

    const validatePermissions = useCallback((rightCodes: string[]) => {
        if (!rightCodes.length) {
            addError({
                controlId: 'profile-right-codes',
                code: 'PERMISSIONS_IS_REQUIRED',
                message: PROFILE_VALIDATION_ERRORS.PERMISSIONS_IS_REQUIRED,
            });
            setErrorMessage(PROFILE_VALIDATION_ERRORS.PERMISSIONS_IS_REQUIRED);
            return true;
        } else {
            removeError('profile-right-codes');
            return false;
        }
    }, [addError, removeError, setErrorMessage]);

    const validateDescription = useCallback((description?: string) => {
        if (!description) {
            addError({
                controlId: 'profile-description',
                code: 'DESCRIPTION_IS_REQUIRED',
                message: PROFILE_VALIDATION_ERRORS.DESCRIPTION_IS_REQUIRED,
            });
            return true;
        } else {
            removeError('profile-description');
            return false;
        }
    }, [addError, removeError]);


    return {
        clearErrors,
        validateDescription,
        validatePermissions,
        validateProfileName,
    };
};
